import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ApiClient, type ExpertType } from "../core/api-client.js";
import { LeadMagnetWidgetBase } from "../core/widget-base.js";
import { widgetStyles } from "../core/widget-styles.js";
import { DEFAULT_IFI_COUNTRIES } from "../lead-magnets/ifi/countries.js";
import type { IfiCountryOption } from "../lead-magnets/ifi/ui-state.js";
import {
  empruntNonResidentFormSchema,
  empruntNonResidentStepSchemas,
} from "../lead-magnets/emprunt-non-resident/form-schema.js";
import { mapEmpruntNonResidentFormToSubmitPayload } from "../lead-magnets/emprunt-non-resident/request-mapper.js";
import {
  getEmpruntNonResidentFormProgress,
  getInitialEmpruntNonResidentFormStep,
  getNextEmpruntNonResidentFormStep,
  getPreviousEmpruntNonResidentFormStep,
  type EmpruntNonResidentFormModel,
  type EmpruntNonResidentFormStep,
  type EmpruntNonResidentResultModel,
  type EmpruntNonResidentWidgetViewState,
} from "../lead-magnets/emprunt-non-resident/ui-state.js";

const STATUT_OPTIONS = [
  "Salarié dans une grande entreprise",
  "Salarié dans une grande entreprise depuis moins de 2 ans",
  "Salarié en PME avec plus de 2 ans d'ancienneté",
  "Salarié en PME depuis moins de 2 ans",
  "Fonctionnaire / secteur public",
  "Profession libérale établie",
  "Indépendant / freelance depuis plus de 3 ans",
  "Entrepreneur depuis moins de 3 ans",
  "Fondateur de startup depuis 1 an",
  "CDD / intérimaire / sans emploi",
  "Cadre salarié en banque",
] as const;

const REVENU_OPTIONS = [
  "Moins de 3 000 €",
  "3 000 - 5 000 €",
  "5 000 - 8 000 €",
  "8 000 - 15 000 €",
  "15 000 - 25 000 €",
  "Plus de 25 000 €",
] as const;

const APPORT_OPTIONS = [
  "Moins de 10 %",
  "10 - 20 %",
  "20 - 30 %",
  "Plus de 30 %",
] as const;

const COUNTRY_DATASET_URL =
  "https://raw.githubusercontent.com/datasets/country-list/main/data.csv";
const frenchRegionDisplayNames =
  typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["fr"], { type: "region" })
    : null;

@customElement("lead-magnet-emprunt-non-resident-widget")
export class LeadMagnetEmpruntNonResidentWidget extends LeadMagnetWidgetBase {
  @property({ type: String })
  public override slug = "emprunt-non-resident";

  @property({ type: String, attribute: "api-base-url" })
  public apiBaseUrl = "";

  @property({ type: String })
  public override source = "landing_emprunt_non_resident_widget";

  @state()
  private viewState: EmpruntNonResidentWidgetViewState = "idle";

  @state()
  private step: EmpruntNonResidentFormStep =
    getInitialEmpruntNonResidentFormStep();

  @state()
  private form: EmpruntNonResidentFormModel = {
    email: "",
    firstName: "",
    source: "landing_emprunt_non_resident_widget",
    consent: false,
    paysResidence: "",
    statutProfessionnel: STATUT_OPTIONS[0],
    revenuMensuel: REVENU_OPTIONS[2],
    chargesMensuelles: "",
    apportDisponible: APPORT_OPTIONS[1],
    garantie: "Oui",
    revenuCapital: "",
    dettesAssociees: "",
    expertComptable: false,
    courtier: false,
    chasseurImmobilier: false,
    honeypot: "",
  };

  @state()
  private resultData: EmpruntNonResidentResultModel | null = null;

  @state()
  private expertMessage = "";

  @state()
  private countries: IfiCountryOption[] = [];

  @state()
  private countrySearch = "";

  @state()
  private countryDropdownOpen = false;

  @state()
  private countryLoading = false;

  public static override styles = widgetStyles;

  public override connectedCallback(): void {
    super.connectedCallback();
    void this.loadCountries();
  }

  protected override render() {
    const integrationError = this.getIntegrationError();
    if (integrationError) {
      return html`<p class="error">${integrationError}</p>`;
    }

    const progress = getEmpruntNonResidentFormProgress(this.step);
    const isIntroStep = this.step === "intro";
    const isContactStep = this.step === "contact";
    const isResultStep = this.step === "result";
    const hasPrevious = ["profil", "finances", "contact", "result"].includes(
      this.step,
    );

    return html`
      <form @submit=${this.onSubmit}>
        ${progress.current > 0
          ? html`<p class="step">
              Étape ${progress.current}/${progress.total}
            </p>`
          : null}
        ${this.renderCurrentStep()}

        <label style="display:none">
          Champ anti-bot
          <input
            name="honeypot"
            type="text"
            tabindex="-1"
            autocomplete="off"
            .value=${this.form.honeypot}
            @input=${(e: Event) => {
              this.form = {
                ...this.form,
                honeypot: (e.target as HTMLInputElement).value,
              };
            }}
          />
        </label>

        ${this.errorMessage
          ? html`<p class="error">${this.errorMessage}</p>`
          : null}
        <div class="actions">
          ${hasPrevious
            ? html`
                <button
                  type="button"
                  class="secondary"
                  ?disabled=${this.isLoading}
                  @click=${this.onPreviousStep}
                >
                  Précédent
                </button>
              `
            : null}
          ${isContactStep
            ? html`
                <button ?disabled=${this.isLoading}>
                  ${this.isLoading
                    ? "Analyse en cours..."
                    : "Recevoir mon score d'éligibilité"}
                </button>
              `
            : isResultStep
              ? html`
                  <button
                    type="button"
                    class="secondary"
                    ?disabled=${this.isLoading}
                    @click=${this.resetFlow}
                  >
                    Recommencer
                  </button>
                `
              : html`
                  <button
                    type="button"
                    ?disabled=${this.isLoading}
                    @click=${this.onNextStep}
                  >
                    ${isIntroStep ? "Commencer" : "Suivant"}
                  </button>
                `}
        </div>
      </form>
    `;
  }

  private renderCurrentStep() {
    switch (this.step) {
      case "intro":
        return this.renderIntroStep();
      case "profil":
        return this.renderProfilStep();
      case "finances":
        return this.renderFinancesStep();
      case "contact":
        return this.renderContactStep();
      case "result":
        return this.renderResultStep();
      default:
        return html``;
    }
  }

  private renderIntroStep() {
    return html`
      <div class="intro-badge">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        Gratuit - 2 min
      </div>

      <h2 class="intro-title">
        Teste ton éligibilité à un crédit immobilier non résident
      </h2>
      <p class="intro-copy">
        Obtiens un premier score réaliste selon ton pays de résidence, ton
        profil pro, ton apport et tes charges.
      </p>

      <div class="intro-benefits">
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Score d'éligibilité instantané</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Points forts et freins de ton dossier</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Aides concrètes pour améliorer tes chances</span>
        </div>
      </div>

      <div class="intro-footer">
        <span>🔒 Tes données restent confidentielles</span>
      </div>
    `;
  }

  private renderProfilStep() {
    const options = this.getFilteredCountries();
    return html`
      <label class="country-box">
        Pays de résidence
        <input
          name="paysResidence"
          type="text"
          placeholder="Commence à taper ton pays..."
          .value=${this.countrySearch}
          @focus=${() => {
            this.countryDropdownOpen = true;
          }}
          @blur=${() => {
            window.setTimeout(() => {
              this.countryDropdownOpen = false;
            }, 120);
          }}
          @input=${(e: Event) => {
            const value = (e.target as HTMLInputElement).value;
            this.countrySearch = value;
            this.countryDropdownOpen = true;
            this.form = { ...this.form, paysResidence: "" };
          }}
        />
        ${this.countryDropdownOpen
          ? html`
              <div class="country-options">
                ${this.countryLoading
                  ? html`<div class="country-option">Chargement...</div>`
                  : options.length > 0
                    ? options.map(
                        (option) => html`
                          <button
                            type="button"
                            class="country-option"
                            @mousedown=${(event: Event) => {
                              event.preventDefault();
                            }}
                            @click=${() => this.selectCountry(option)}
                          >
                            <span>${option.name}</span>
                            <span class="country-code">${option.code}</span>
                          </button>
                        `,
                      )
                    : html`<div class="country-option">Aucun résultat</div>`}
              </div>
            `
          : null}
      </label>

      <label>
        Statut professionnel
        <select
          name="statutProfessionnel"
          .value=${this.form.statutProfessionnel}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              statutProfessionnel: (e.target as HTMLSelectElement).value,
            };
          }}
        >
          ${STATUT_OPTIONS.map(
            (option) => html`<option value=${option}>${option}</option>`,
          )}
        </select>
      </label>

      <label>
        Revenu mensuel
        <select
          name="revenuMensuel"
          .value=${this.form.revenuMensuel}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              revenuMensuel: (e.target as HTMLSelectElement).value,
            };
          }}
        >
          ${REVENU_OPTIONS.map(
            (option) => html`<option value=${option}>${option}</option>`,
          )}
        </select>
      </label>

      <label>
        Apport disponible
        <select
          name="apportDisponible"
          .value=${this.form.apportDisponible}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              apportDisponible: (e.target as HTMLSelectElement).value,
            };
          }}
        >
          ${APPORT_OPTIONS.map(
            (option) => html`<option value=${option}>${option}</option>`,
          )}
        </select>
      </label>
    `;
  }

  private renderFinancesStep() {
    return html`
      <label>
        Charges mensuelles (€)
        <input
          name="chargesMensuelles"
          type="text"
          inputmode="numeric"
          placeholder="1 200"
          .value=${this.formatThousands(this.form.chargesMensuelles)}
          @input=${(e: Event) =>
            this.updateIntegerField(
              "chargesMensuelles",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>

      <label>
        Garantie disponible
        <select
          name="garantie"
          .value=${this.form.garantie}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              garantie: (e.target as HTMLSelectElement).value as "Oui" | "Non",
            };
          }}
        >
          <option value="Oui">Oui</option>
          <option value="Non">Non</option>
        </select>
      </label>

      <label>
        Revenus de capital mensuels (optionnel)
        <input
          name="revenuCapital"
          type="text"
          inputmode="numeric"
          placeholder="2 000"
          .value=${this.formatThousands(this.form.revenuCapital)}
          @input=${(e: Event) =>
            this.updateIntegerField(
              "revenuCapital",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>

      <label>
        Dettes associées mensuelles (optionnel)
        <input
          name="dettesAssociees"
          type="text"
          inputmode="numeric"
          placeholder="500"
          .value=${this.formatThousands(this.form.dettesAssociees)}
          @input=${(e: Event) =>
            this.updateIntegerField(
              "dettesAssociees",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>

      <p class="helper">
        Ajoute tes autres dettes ou revenus de capital si tu veux une estimation
        plus fine.
      </p>
    `;
  }

  private renderContactStep() {
    return html`
      <div class="preview-card">
        <div class="preview-row">
          <span class="preview-label">Pays</span>
          <span class="preview-value">${this.form.paysResidence || "—"}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Profil</span>
          <span class="preview-value">${this.form.statutProfessionnel}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Revenus</span>
          <span class="preview-value">${this.form.revenuMensuel}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Apport</span>
          <span class="preview-value">${this.form.apportDisponible}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Score estimé</span>
          <span class="preview-value blurred">██ %</span>
        </div>
      </div>

      <p class="unlock-title">Débloquer mon analyse complète</p>

      <label>
        Email
        <input
          name="email"
          type="email"
          placeholder="ton@email.com"
          .value=${this.form.email}
          @input=${(e: Event) => {
            this.form = {
              ...this.form,
              email: (e.target as HTMLInputElement).value,
            };
          }}
          required
        />
      </label>

      <label>
        Prénom
        <input
          name="firstName"
          type="text"
          placeholder="Ton prénom"
          .value=${this.form.firstName}
          @input=${(e: Event) => {
            this.form = {
              ...this.form,
              firstName: (e.target as HTMLInputElement).value,
            };
          }}
        />
      </label>

      <label style="display: flex; align-items: center; gap: 8px;">
        <input
          name="consent"
          type="checkbox"
          .checked=${this.form.consent}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              consent: (e.target as HTMLInputElement).checked,
            };
          }}
          required
        />
        <span>J'accepte de recevoir mon score par email.</span>
      </label>
    `;
  }

  private renderResultStep() {
    if (!this.resultData) {
      return html`
        <div class="result-card">
          <p class="result-title">Résultat indisponible</p>
          <p class="helper">
            Impossible d'afficher le résultat pour le moment.
          </p>
        </div>
      `;
    }

    const toneClass =
      this.resultData.score >= 65
        ? "positive"
        : this.resultData.score < 35
          ? "negative"
          : "";

    return html`
      <div class="result-card">
        <p class="result-title ${toneClass}">
          Score d'éligibilité: ${Math.round(this.resultData.score)}%
        </p>

        <div class="preview-card">
          <div class="preview-row">
            <span class="preview-label">Pays de résidence</span>
            <span class="preview-value">${this.form.paysResidence}</span>
          </div>
          <div class="preview-row">
            <span class="preview-label">Apport</span>
            <span class="preview-value">${this.form.apportDisponible}</span>
          </div>
          <div class="preview-row">
            <span class="preview-label">Garantie</span>
            <span class="preview-value">${this.form.garantie}</span>
          </div>
        </div>

        <div>
          <p class="result-title" style="font-size: 14px;">Points forts</p>
          <ul style="margin: 0; padding-left: 18px; display: grid; gap: 6px;">
            ${this.resultData.pointsForts.map((item) => html`<li>${item}</li>`)}
          </ul>
        </div>

        <div>
          <p class="result-title" style="font-size: 14px;">
            Points d'attention
          </p>
          <ul style="margin: 0; padding-left: 18px; display: grid; gap: 6px;">
            ${this.resultData.pointsFaibles.map(
              (item) => html`<li>${item}</li>`,
            )}
          </ul>
        </div>
        ${this.renderExpertSection()}
      </div>
    `;
  }

  private onNextStep = (): void => {
    if (!this.validateStep(this.step)) {
      this.viewState = "error";
      return;
    }
    this.trackStep(this.step);
    this.viewState = "idle";
    this.step = getNextEmpruntNonResidentFormStep(this.step);
  };

  private onPreviousStep = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getPreviousEmpruntNonResidentFormStep(this.step);
  };

  private renderExpertSection() {
    return html`
      <div class="expert-card" style="margin-top: 0.75rem;">
        <span class="expert-eyebrow">Accompagnement</span>
        <p class="expert-title">
          Tu veux étudier plus en détail ta capacité d'emprunt avec un
          partenaire de confiance ?
        </p>
        <p class="expert-copy">
          Choisis les partenaires que tu veux solliciter pour avancer sur ton
          projet de financement ou de recherche immobilière.
        </p>

        <div class="expert-options">
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.expertComptable}
              @change=${() => {
                this.form = {
                  ...this.form,
                  expertComptable: !this.form.expertComptable,
                };
              }}
            />
            <span>Expert-comptable</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.courtier}
              @change=${() => {
                this.form = { ...this.form, courtier: !this.form.courtier };
              }}
            />
            <span>Courtier en financement</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.chasseurImmobilier}
              @change=${() => {
                this.form = {
                  ...this.form,
                  chasseurImmobilier: !this.form.chasseurImmobilier,
                };
              }}
            />
            <span>Chasseur immobilier</span>
          </label>
        </div>

        <button
          type="button"
          ?disabled=${this.isLoading}
          @click=${this.submitExpertOptin}
        >
          ${this.isLoading
            ? "Envoi en cours..."
            : "Être mis en relation avec ces experts"}
        </button>

        ${this.expertMessage
          ? html`<p class="expert-feedback ${this.expertMessage.startsWith("Sélectionne") ? "error" : ""}">${this.expertMessage}</p>`
          : null}
      </div>
    `;
  }

  private onSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (this.step !== "contact") {
      this.onNextStep();
      return;
    }

    if (!this.validateStep("contact")) {
      this.viewState = "error";
      return;
    }

    const parsed = empruntNonResidentFormSchema.safeParse(
      this.buildFinalValidationInput(),
    );
    if (!parsed.success) {
      this.viewState = "error";
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return;
    }

    this.startLoading();
    this.viewState = "submitting";

    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      const payload = mapEmpruntNonResidentFormToSubmitPayload({
        ...this.form,
        source: parsed.data.source,
        consent: parsed.data.consent,
      });

      const response = await apiClient.submitLeadMagnet(
        "emprunt-non-resident",
        payload,
        this.makeIdempotencyKey(),
      );
      const resultData = this.mapApiResult(response.result.payload);
      this.resultData = resultData;
      this.step = "result";
      this.viewState = "idle";
      this.errorMessage = "";
      this.trackSubmit({
        correlationId: response.correlationId,
        status: "success",
      });
      this.trackResult({
        correlationId: response.correlationId,
        scoreRange: this.getScoreRange(resultData.score),
        status: "success",
      });
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Soumission impossible.";
      this.viewState = "error";
      this.setError(msg);
      this.trackError(this.step, msg, { status: "error" });
    } finally {
      this.stopLoading();
    }
  };

  private validateStep(step: EmpruntNonResidentFormStep): boolean {
    if (step === "result") {
      this.errorMessage = "";
      return true;
    }
    const schema = empruntNonResidentStepSchemas[step];
    const parsed = schema.safeParse(this.buildStepValidationInput(step));
    if (!parsed.success) {
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return false;
    }
    if (step === "contact") {
      this.form = { ...this.form, source: this.getSourceValue() };
    }
    this.errorMessage = "";
    return true;
  }

  private buildStepValidationInput(
    step: EmpruntNonResidentFormStep,
  ): Record<string, unknown> {
    switch (step) {
      case "profil":
        return {
          paysResidence: this.form.paysResidence,
          statutProfessionnel: this.form.statutProfessionnel,
          revenuMensuel: this.form.revenuMensuel,
          apportDisponible: this.form.apportDisponible,
          garantie: this.form.garantie,
        };
      case "finances":
        return {
          chargesMensuelles: this.form.chargesMensuelles,
          revenuCapital: this.form.revenuCapital,
          dettesAssociees: this.form.dettesAssociees,
        };
      case "contact":
        return {
          email: this.form.email,
          firstName: this.form.firstName,
          source: this.getSourceValue(),
          consent: this.form.consent,
          honeypot: this.form.honeypot,
        };
      default:
        return {};
    }
  }

  private buildFinalValidationInput(): Record<string, unknown> {
    return { ...this.form, source: this.getSourceValue() };
  }

  private submitExpertOptin = async (): Promise<void> => {
    const expertTypes = this.getExpertTypes();
    if (expertTypes.length === 0) {
      this.expertMessage = "Sélectionne au moins un expert pour être mis en relation.";
      return;
    }
    this.startLoading();
    this.errorMessage = "";
    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      await apiClient.submitExpertOptin("emprunt-non-resident", {
        email: this.form.email.trim(),
        firstName: this.form.firstName.trim() || undefined,
        source: this.getSourceValue(),
        expertTypes,
      });
      this.trackCustomEvent("lm_expert_optin_submitted", {
        expertTypes,
        expertCount: expertTypes.length,
        scoreRange: this.resultData
          ? this.getScoreRange(this.resultData.score)
          : undefined,
        status: "success",
      });
      this.expertMessage =
        expertTypes.length > 0
          ? "Parfait, ta demande a bien été enregistrée."
          : "Merci, ton analyse est terminée.";
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Soumission impossible.";
      this.errorMessage = msg;
      this.trackError("result", msg, { status: "error" });
    } finally {
      this.stopLoading();
    }
  };

  private resetFlow = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getInitialEmpruntNonResidentFormStep();
    this.resultData = null;
    this.expertMessage = "";
    this.countrySearch = "";
    this.countryDropdownOpen = false;
    this.form = {
      email: "",
      firstName: "",
      source: this.getSourceValue(),
      consent: false,
      paysResidence: "",
      statutProfessionnel: STATUT_OPTIONS[0],
      revenuMensuel: REVENU_OPTIONS[2],
      chargesMensuelles: "",
      apportDisponible: APPORT_OPTIONS[1],
      garantie: "Oui",
      revenuCapital: "",
      dettesAssociees: "",
      expertComptable: false,
      courtier: false,
      chasseurImmobilier: false,
      honeypot: "",
    };
  };

  private getExpertTypes(): ExpertType[] {
    const expertTypes: ExpertType[] = [];
    if (this.form.expertComptable) expertTypes.push("expert_comptable");
    if (this.form.courtier) expertTypes.push("courtier");
    if (this.form.chasseurImmobilier) {
      expertTypes.push("chasseur_immobilier");
    }
    return expertTypes;
  }

  private mapApiResult(
    payload: Record<string, unknown>,
  ): EmpruntNonResidentResultModel {
    return {
      score: toNumber(payload.score) ?? 0,
      conseil: typeof payload.conseil === "string" ? payload.conseil : "",
      pointsForts: toStringArray(payload.points_forts),
      pointsFaibles: toStringArray(payload.points_faibles),
    };
  }

  private makeIdempotencyKey(): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `enr_${crypto.randomUUID()}`;
    }
    return `enr_${Date.now()}`;
  }

  private getScoreRange(score: number): string {
    if (score < 35) return "faible";
    if (score < 65) return "moyen";
    return "élevé";
  }

  private getSourceValue(): string {
    return this.source.trim() || this.form.source;
  }

  private async loadCountries(): Promise<void> {
    this.countryLoading = true;
    this.countries = normalizeCountriesForFrench(DEFAULT_IFI_COUNTRIES);
    try {
      const response = await fetch(COUNTRY_DATASET_URL);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const csv = await response.text();
      const parsedCountries = normalizeCountriesForFrench(
        parseCountriesCsv(csv),
      );
      if (parsedCountries.length >= DEFAULT_IFI_COUNTRIES.length) {
        this.countries = parsedCountries;
      }
    } catch {
      this.countries = normalizeCountriesForFrench(DEFAULT_IFI_COUNTRIES);
    } finally {
      this.countryLoading = false;
    }
  }

  private getFilteredCountries(): IfiCountryOption[] {
    const query = stripDiacritics(this.countrySearch.trim().toLowerCase());
    if (!query) {
      return this.countries.slice(0, 12);
    }
    return this.countries
      .filter((country) =>
        stripDiacritics(country.name.toLowerCase()).includes(query),
      )
      .slice(0, 12);
  }

  private selectCountry(country: IfiCountryOption): void {
    this.form = { ...this.form, paysResidence: country.name };
    this.countrySearch = country.name;
    this.countryDropdownOpen = false;
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    this.errorMessage = "";
  }

  private updateIntegerField(
    field: "chargesMensuelles" | "revenuCapital" | "dettesAssociees",
    value: string,
  ): void {
    this.form = { ...this.form, [field]: value.replaceAll(/\D+/g, "") };
  }

  private formatThousands(value: string): string {
    const digits = value.replaceAll(/\D+/g, "").replace(/^0+(?=\d)/, "");
    if (!digits) return "";
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  private getIntegrationError(): string | null {
    if (this.slug !== "emprunt-non-resident") {
      return 'Ce widget supporte uniquement le slug "emprunt-non-resident".';
    }
    const baseUrl = this.apiBaseUrl.trim();
    if (!baseUrl)
      return "Configuration invalide: l'attribut `api-base-url` est obligatoire.";
    try {
      const parsed = new URL(baseUrl);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "Configuration invalide: `api-base-url` doit utiliser http ou https.";
      }
    } catch {
      return "Configuration invalide: `api-base-url` doit être une URL absolue.";
    }
    return null;
  }
}

function stripDiacritics(text: string): string {
  return text.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "");
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.replaceAll(/\s+/g, "").replace(",", "."));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is string =>
      typeof item === "string" && item.trim().length > 0,
  );
}

function parseCountriesCsv(csv: string): IfiCountryOption[] {
  const lines = csv.split(/\r?\n/).filter((line) => line.trim().length > 0);
  const dataLines = lines.slice(1);
  const countries: IfiCountryOption[] = [];

  for (const line of dataLines) {
    const parsed = parseCsvLine(line);
    if (parsed.length < 2) {
      continue;
    }
    const name = parsed[0]?.trim();
    const code = parsed[1]?.trim().toUpperCase();
    if (!name || !code) {
      continue;
    }
    countries.push({ name, code });
  }

  return countries.sort((a, b) => a.name.localeCompare(b.name));
}

function normalizeCountriesForFrench(
  countries: IfiCountryOption[],
): IfiCountryOption[] {
  const uniqueByCode = new Map<string, IfiCountryOption>();
  for (const country of countries) {
    const code = country.code.trim().toUpperCase();
    if (!code) {
      continue;
    }
    uniqueByCode.set(code, {
      code,
      name: toFrenchCountryName(code, country.name),
    });
  }

  return Array.from(uniqueByCode.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "fr"),
  );
}

function toFrenchCountryName(code: string, fallback: string): string {
  const fromIntl = frenchRegionDisplayNames?.of(code);
  if (fromIntl && fromIntl.trim().length > 0 && fromIntl !== code) {
    return fromIntl;
  }
  return fallback;
}

function parseCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }
    current += char;
  }

  values.push(current);
  return values;
}

declare global {
  interface HTMLElementTagNameMap {
    "lead-magnet-emprunt-non-resident-widget": LeadMagnetEmpruntNonResidentWidget;
  }
}
